/**
 * Typed failure modes of reading usage (spec 072 FR-004).
 *
 * Outcomes a reader must be able to tell apart: "you may not see this", "we could not
 * find out", and — since spec 136 — "you are no longer signed in". The page switches on
 * `kind`, never on message text, so the distinction survives any rewording of the copy —
 * the same reasoning `WorkspaceDeleteError` records.
 *
 * The first is not an error in the ordinary sense. Only a workspace's owner may read its
 * usage, so a refusal is the expected answer for everyone else and deserves an
 * explanation rather than an error boundary.
 */

/**
 * `auth-expired` is the third outcome, added by spec 136: a 401 means the session
 * went away and the answer is to sign in again, not to press Try again — which is
 * what every reader of `unavailable` is told to do. It is a `kind` rather than a
 * message because pages switch on `kind`, per this file's own rule.
 */
export type UsageErrorKind = 'forbidden' | 'unavailable' | 'auth-expired';

/** What the server said, for the console rather than for the page (spec 136 R-F35). */
export interface UsageErrorDetail {
  /** The HTTP status, so a 401, a 429 and a 500 are told apart in a bug report. */
  status: number;
  /** The error envelope's `error`/`code`, when the response carried one. */
  code?: string;
}

export class UsageError extends Error {
  readonly kind: UsageErrorKind;
  /**
   * Absent on the many call sites that predate it, and deliberately **not** rendered:
   * a status code on screen tells a reader nothing they can act on. It exists so the
   * log line and the bug report can say which failure this was.
   */
  readonly detail?: UsageErrorDetail;

  constructor(
    kind: UsageErrorKind,
    message: string,
    detail?: UsageErrorDetail,
  ) {
    super(message);
    this.name = 'UsageError';
    this.kind = kind;
    this.detail = detail;
  }
}

export function isForbidden(error: unknown): boolean {
  return error instanceof UsageError && error.kind === 'forbidden';
}

/** The session went away — reloading signs in again, retrying does not help. */
export function isAuthExpired(error: unknown): boolean {
  return error instanceof UsageError && error.kind === 'auth-expired';
}
